import React from 'react';
import Alert from 'react-bootstrap/Alert';
import Badge from 'react-bootstrap/Badge';
import Modal from 'react-bootstrap/Modal';
import { Button } from '../../../common-components/Button/Button';
import { FormatHelper } from '../../../helpers/FormatHelper';
import { OrderPendingInvoicingHistoryItem } from '../../../services/OrderPendingInvoicingHistories/GetOrderPendingInvoicingHistoryList/GetOrderPendingInvoicingHistoryListOutput';
import {
  formatMinutes,
  getChannelText,
  getDeliveryText,
  getSeparationText,
  getShopifyAdminOrderUrl,
  getSituationText,
  hints
} from '../pendingInvoicingOrderUi';
import {
  fieldLabel,
  formatAttempts,
  getAttempts,
  getResolutionSteps,
  integrationStatusLabel,
  lifecycleLabel,
  severityVariant
} from '../pendingInvoicingResolution';
import {
  ActionCard,
  ActionDescription,
  ActionIndex,
  ActionTitle,
  Code,
  EmptyText,
  HintLabel,
  InfoGrid,
  InfoItem,
  InfoLabel,
  InfoValue,
  Section,
  SectionTitle,
  Subtitle,
  TechnicalToggle,
  TitleRow
} from './styles';

interface IModalPendingInvoicingOrderDetailsProps {
  item: OrderPendingInvoicingHistoryItem;
  close: () => void;
}

const situationVariant: Record<string, string> = {
  Pendente: 'warning',
  Finalizado: 'success'
};

const ModalPendingInvoicingOrderDetails: React.FC<IModalPendingInvoicingOrderDetailsProps> = ({
  item,
  close
}) => {

  const [technicalVisible, setTechnicalVisible] = React.useState<boolean>(false);

  const situation = getSituationText(item);
  const alert = item.alert;
  const attempts = getAttempts(item);
  const steps = React.useMemo(() => getResolutionSteps(item), [item]);
  const affectedFields: string[] = Array.isArray(alert?.affectedFields) ? alert!.affectedFields : [];

  return (
    <Modal
      show={true}
      onHide={() => close()}
      size='lg'
    >
      <Modal.Header closeButton>
        <div>
          <TitleRow>
            <Modal.Title>Pedido #{item.number}</Modal.Title>
            <Badge bg={situationVariant[situation] || 'secondary'} text={situation === 'Pendente' ? 'dark' : undefined}>
              {situation}
            </Badge>
          </TitleRow>
          {item.stateDescription ? <Subtitle>{item.stateDescription}</Subtitle> : null}
        </div>
      </Modal.Header>

      <Modal.Body style={{ maxHeight: '75vh', overflowY: 'auto' }}>
        <InfoGrid>
          {info('Pago em', FormatHelper.formatDateTime(item.paidAt))}
          {info(item.isPending ? 'Tempo desde o pagamento' : 'Do pagamento até sair', formatMinutes(item.minutesSincePayment), hints.time)}
          {info(item.isPending ? 'Pendente desde' : 'Saiu da pendência em',
            FormatHelper.formatDateTime(item.isPending ? item.currentPendingSince : item.pendingEndedAt),
            item.isPending ? hints.pendingSince : hints.endedAt)}
          {info('Vezes que ficou pendente', item.pendingEntriesCount, hints.entries)}
        </InfoGrid>

        <Section>
          <SectionTitle>Motivo</SectionTitle>
          {alert?.message ? (
            <Alert variant={severityVariant(alert.severity)} className='mb-2'>
              <strong>{alert.message}</strong>
              {alert.suggestion ? <div className='mt-1'>{alert.suggestion}</div> : null}
              {affectedFields.length > 0 ? (
                <div className='mt-2'>
                  {affectedFields.map(field => (
                    <Badge bg='secondary' className='me-1' key={field}>{fieldLabel(field)}</Badge>
                  ))}
                </div>
              ) : null}
            </Alert>
          ) : (
            <EmptyText>O Quatro Olhos ainda não informou o motivo deste pedido.</EmptyText>
          )}
          {item.diagnosticError ? (
            <Alert variant='light' className='mb-0 py-2'>
              <small>
                A última consulta ao Quatro Olhos{item.lastCheckedAt ? ` (${FormatHelper.formatDateTime(item.lastCheckedAt)})` : ''} falhou.
                {alert?.message ? ' O motivo acima é o da consulta anterior.' : ''}
              </small>
            </Alert>
          ) : null}
        </Section>

        <Section>
          <SectionTitle>O que fazer</SectionTitle>
          {steps.actions.map((action, index) => (
            <ActionCard key={action.code || index}>
              <ActionIndex>{index + 1}</ActionIndex>
              <div>
                <ActionTitle>{action.title}</ActionTitle>
                <ActionDescription>{action.description}</ActionDescription>
                {action.fields.length > 0 ? (
                  <div className='mt-1'>
                    {action.fields.map(field => (
                      <Badge bg='light' text='dark' className='me-1 border' key={field}>{fieldLabel(field)}</Badge>
                    ))}
                  </div>
                ) : null}
              </div>
            </ActionCard>
          ))}
          {steps.notes.map(note => (
            <Alert variant='info' className='mb-2 py-2' key={note}>{note}</Alert>
          ))}
          {steps.actions.length === 0 && steps.notes.length === 0 ? (
            <EmptyText>O Quatro Olhos não indicou nenhuma ação para este pedido.</EmptyText>
          ) : null}
        </Section>

        <Section>
          <SectionTitle>Integração no Quatro Olhos</SectionTitle>
          <InfoGrid>
            {info('Situação', lifecycleLabel(item.lifecycle))}
            {info('Status da integração', integrationStatusLabel(item.integrationStatus))}
            {info('Tentativas', formatAttempts(attempts))}
            {info('Última tentativa', FormatHelper.formatDateTime(attempts?.lastAttemptAt))}
            {info('Próxima tentativa automática', attempts
              ? FormatHelper.formatDateTime(attempts.nextAttemptAt) || (attempts.automaticRetryAvailable ? 'Aguardando agenda' : 'Não haverá')
              : '')}
          </InfoGrid>
        </Section>

        <Section>
          <SectionTitle>Pedido</SectionTitle>
          <InfoGrid>
            {info('Tipo', getSeparationText(item))}
            {info('Entrega', getDeliveryText(item))}
            {info('Canal', getChannelText(item))}
            {info('Id do pedido na Shopify', item.shopifyId ? (
              <a href={getShopifyAdminOrderUrl(item.shopifyId)} target='_blank' rel='noopener noreferrer'>
                {item.shopifyId}
              </a>
            ) : null)}
          </InfoGrid>
        </Section>

        <TechnicalToggle type='button' onClick={() => setTechnicalVisible(!technicalVisible)}>
          {technicalVisible ? 'Ocultar detalhes técnicos' : 'Ver detalhes técnicos'}
        </TechnicalToggle>
        {technicalVisible ? (
          <Section>
            <InfoGrid>
              {info('Código do alerta', alert?.code)}
              {info('Categoria', alert?.category)}
              {info('Primeira entrada na pendência', FormatHelper.formatDateTime(item.firstPendingAt))}
              {info('Última consulta ao Quatro Olhos', FormatHelper.formatDateTime(item.lastCheckedAt))}
            </InfoGrid>
            {alert?.technicalMessage ? (
              <>
                <InfoLabel>Mensagem técnica</InfoLabel>
                <Code>{alert.technicalMessage}</Code>
              </>
            ) : null}
            {item.diagnosticError ? (
              <>
                <InfoLabel>Erro da última consulta</InfoLabel>
                <Code>{item.diagnosticError}</Code>
              </>
            ) : null}
          </Section>
        ) : null}
      </Modal.Body>

      <Modal.Footer>
        <Button text={'Fechar'} click={() => close()} />
      </Modal.Footer>
    </Modal>
  );

  function info(label: string, value: any, hint?: string) {
    if (value === undefined || value === null || value === '') return null;
    return (
      <InfoItem title={hint}>
        <InfoLabel>{hint ? <HintLabel>{label}</HintLabel> : label}</InfoLabel>
        <InfoValue>{value}</InfoValue>
      </InfoItem>
    );
  }
}

export { ModalPendingInvoicingOrderDetails };
